import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UsersThree, Copy, Plus, SignOut, Flame, Lightning, Trophy, PaperPlaneRight, Trash } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function Team() {
  const { user, refresh } = useAuth();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [coach, setCoach] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState("");
  const [loading, setLoading] = useState(true);

  // Coach form
  const [teamName, setTeamName] = useState("");

  // Player form
  const [joinCode, setJoinCode] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/mine");
      if (data) {
        setTeam(data.team);
        setMembers(data.members);
        setCoach(data.coach);
        try {
          const lb = await api.get("/team/leaderboard");
          setLeaderboard(lb.data);
        } catch {}
      } else {
        setTeam(null);
      }
    } catch { setTeam(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Poll messages every 5s when team is loaded
  useEffect(() => {
    if (!team) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/team/messages");
        setMessages(data);
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [team]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgInput.trim()) return;
    try {
      const { data } = await api.post("/team/messages", { content: msgInput });
      setMessages([...messages, data]);
      setMsgInput("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const deleteMessage = async (id) => {
    try {
      await api.delete(`/team/messages/${id}`);
      setMessages(messages.filter((m) => m.id !== id));
    } catch { toast.error("Could not delete"); }
  };

  const createTeam = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team/create", { name: teamName });
      toast.success("Team created");
      setTeamName("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const joinTeam = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team/join", { code: joinCode });
      toast.success("Joined team");
      setJoinCode("");
      await refresh();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const leave = async () => {
    if (!confirm("Leave the team? Your Team-tier access will be removed.")) return;
    try {
      await api.post("/team/leave");
      await refresh();
      load();
      toast.success("Left team");
    } catch { toast.error("Failed"); }
  };

  const isCoach = user?.role === "coach";
  const hasTeamSub = user?.subscription_tier === "team";

  if (loading) {
    return <Layout><div className="p-12 text-[#A3A3A3]">Loading…</div></Layout>;
  }

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="mb-10">
          <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Squad</div>
          <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
            <UsersThree size={48} weight="duotone" className="text-[#FF3B30]" /> Team
          </h1>
        </div>

        {/* Coach without team subscription */}
        {isCoach && !hasTeamSub && !team && (
          <div className="border border-[#FF3B30] bg-[#FF3B30]/10 p-8 text-center" data-testid="team-upgrade">
            <div className="font-heading font-black uppercase text-2xl mb-2">Team Subscription Required</div>
            <p className="text-[#A3A3A3] mb-6">Manage your entire squad on one licence: 1 coach + 14 player seats. All seats unlock full Team-tier access.</p>
            <Link to="/pricing" className="inline-block bg-[#FF3B30] hover:bg-[#D63026] text-white px-6 py-3 font-heading uppercase tracking-widest text-sm font-bold">See Team Plan</Link>
          </div>
        )}

        {/* Coach with subscription but no team */}
        {isCoach && hasTeamSub && !team && (
          <div className="border border-[#262626] bg-[#121212] p-8 max-w-lg" data-testid="create-team">
            <div className="font-heading font-black uppercase text-2xl mb-4">Create Your Team</div>
            <form onSubmit={createTeam} className="space-y-4">
              <div>
                <Label className="font-heading uppercase tracking-widest text-xs">Team Name</Label>
                <Input required value={teamName} onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g. Northside RFC First XV" data-testid="team-name"
                  className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
              </div>
              <Button type="submit" data-testid="create-team-submit"
                className="w-full rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11">
                Create Team
              </Button>
            </form>
          </div>
        )}

        {/* Player not in team */}
        {!isCoach && !team && (
          <div className="border border-[#262626] bg-[#121212] p-8 max-w-lg" data-testid="join-team">
            <div className="font-heading font-black uppercase text-2xl mb-2">Join a Team</div>
            <p className="text-sm text-[#A3A3A3] mb-4">Got a team code from your coach? Enter it to join and unlock Team-tier features.</p>
            <form onSubmit={joinTeam} className="space-y-4">
              <div>
                <Label className="font-heading uppercase tracking-widest text-xs">Team Code</Label>
                <Input required value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="e.g. FIRSTXV1" data-testid="team-code" maxLength={8}
                  className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11 font-mono uppercase" />
              </div>
              <Button type="submit" data-testid="join-team-submit"
                className="w-full rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11">
                Join Team
              </Button>
            </form>
          </div>
        )}

        {/* Team view */}
        {team && (
          <div className="space-y-6">
            <div className="border border-[#FF3B30] bg-[#FF3B30]/5 p-8 flex items-center justify-between" data-testid="team-info">
              <div>
                <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">Team</div>
                <div className="font-heading font-black uppercase text-4xl mt-1">{team.name}</div>
                {coach && (
                  <div className="text-sm text-[#A3A3A3] mt-2">Coach: <span className="text-white">{coach.name}</span></div>
                )}
                <div className="mt-4 flex items-center gap-4">
                  <div className="text-sm">
                    <span className="text-[#A3A3A3]">Seats:</span> <span className="font-heading font-bold">{(team.member_ids?.length || 0) + 1}/{team.seats}</span>
                  </div>
                  {isCoach && (
                    <div className="flex items-center gap-2 border border-[#FF3B30] px-3 py-1 bg-[#FF3B30]/10">
                      <span className="text-xs uppercase tracking-widest text-[#A3A3A3]">Code</span>
                      <span className="font-heading font-black text-lg">{team.code}</span>
                      <button onClick={() => { navigator.clipboard.writeText(team.code); toast.success("Copied"); }} data-testid="copy-team-code">
                        <Copy size={16} className="text-[#FF3B30]" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {!isCoach && (
                <button onClick={leave} data-testid="leave-team"
                  className="border border-[#262626] text-[#A3A3A3] hover:text-[#FF3B30] hover:border-[#FF3B30] px-4 py-2 font-heading uppercase tracking-widest text-xs flex items-center gap-2">
                  <SignOut size={16} /> Leave
                </button>
              )}
            </div>

            <div className="border border-[#262626] bg-[#121212]">
              <div className="p-4 border-b border-[#262626] text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">Roster</div>
              {members.length === 0 ? (
                <div className="p-8 text-center text-[#A3A3A3] text-sm" data-testid="no-members">
                  No members yet. {isCoach ? `Share code ${team.code} with your squad.` : ""}
                </div>
              ) : (
                <div>
                  {members.map((m) => (
                    <div key={m._id} className="grid grid-cols-12 gap-4 p-4 border-b border-[#1C1C1C] last:border-0 text-sm" data-testid={`member-${m._id}`}>
                      <div className="col-span-4 font-semibold">{m.name}</div>
                      <div className="col-span-3 text-[#A3A3A3]">{m.profile?.position || "No profile"}</div>
                      <div className="col-span-3 text-[#A3A3A3] text-xs">{m.profile ? `${m.profile.current_level?.replace("_", " ")}` : ""}</div>
                      <div className="col-span-2 text-right">
                        <span className="border border-[#BF5AF2] text-[#BF5AF2] px-2 py-0.5 text-xs font-heading uppercase tracking-widest">TEAM</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Leaderboard */}
            {leaderboard && leaderboard.rows && leaderboard.rows.length > 0 && (
              <div className="border border-[#262626] bg-[#121212]" data-testid="leaderboard">
                <div className="p-4 border-b border-[#262626] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy size={20} weight="duotone" className="text-[#FF3B30]" />
                    <div className="font-heading font-bold uppercase tracking-widest text-sm">Weekly Leaderboard</div>
                  </div>
                  <div className="text-xs text-[#A3A3A3]">Week of {leaderboard.week_start}</div>
                </div>
                <div className="grid grid-cols-12 gap-4 p-4 border-b border-[#262626] text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">
                  <div className="col-span-1">#</div>
                  <div className="col-span-4">Player</div>
                  <div className="col-span-2 text-center">This Week</div>
                  <div className="col-span-2 text-center">Streak</div>
                  <div className="col-span-2 text-center">30 Days</div>
                  <div className="col-span-1 text-center">Total</div>
                </div>
                {leaderboard.rows.map((r, i) => (
                  <div key={r.user_id}
                    className={`grid grid-cols-12 gap-4 p-4 border-b border-[#1C1C1C] last:border-0 text-sm items-center ${i === 0 ? "bg-[#FF3B30]/5" : ""}`}
                    data-testid={`leader-${i}`}>
                    <div className="col-span-1 font-heading font-black text-2xl">
                      {i === 0 ? <span className="text-[#FF3B30]">1</span> : (i + 1)}
                    </div>
                    <div className="col-span-4">
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-[#A3A3A3]">{r.position || "—"}</div>
                    </div>
                    <div className="col-span-2 text-center font-heading font-black text-2xl">{r.this_week}</div>
                    <div className="col-span-2 text-center">
                      <div className="inline-flex items-center gap-1">
                        <Flame size={16} weight={r.streak > 0 ? "fill" : "regular"} className={r.streak > 0 ? "text-[#FF3B30]" : "text-[#A3A3A3]"} />
                        <span className="font-heading font-bold">{r.streak}</span>
                      </div>
                    </div>
                    <div className="col-span-2 text-center text-[#A3A3A3]">{r.last_30_days}</div>
                    <div className="col-span-1 text-center text-[#A3A3A3]">{r.total}</div>
                  </div>
                ))}
              </div>
            )}
            {/* Team Chat */}
            <div className="border border-[#262626] bg-[#121212]" data-testid="team-chat">
              <div className="p-4 border-b border-[#262626] flex items-center gap-3">
                <PaperPlaneRight size={20} weight="duotone" className="text-[#FF3B30]" />
                <div className="font-heading font-bold uppercase tracking-widest text-sm">Team Chat</div>
                <span className="text-xs text-[#A3A3A3]">· {messages.length} messages · live</span>
              </div>
              <div className="p-4 max-h-96 overflow-y-auto space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center text-[#A3A3A3] text-sm py-8" data-testid="no-messages">
                    No messages yet. Kick it off — talk match plans, cheer teammates, share reps.
                  </div>
                ) : (
                  messages.map((m) => {
                    const isMe = m.user_id === user._id;
                    const isCoachMsg = m.role === "coach";
                    return (
                      <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                        <div className={`max-w-[75%] border p-3 ${
                          isMe ? "border-[#FF3B30] bg-[#FF3B30]/10" :
                          isCoachMsg ? "border-[#FF9F0A] bg-[#FF9F0A]/5" :
                          "border-[#262626] bg-[#0A0A0A]"
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-heading uppercase tracking-widest ${isCoachMsg ? "text-[#FF9F0A]" : "text-[#A3A3A3]"}`}>
                              {m.name}{isCoachMsg && " · Coach"}
                            </span>
                            <span className="text-[10px] text-[#A3A3A3]">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                            {isMe && (
                              <button onClick={() => deleteMessage(m.id)} className="text-[#A3A3A3] hover:text-[#FF3B30] ml-auto" data-testid={`del-msg-${m.id}`}>
                                <Trash size={12} />
                              </button>
                            )}
                          </div>
                          <div className="text-sm">{m.content}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <form onSubmit={sendMessage} className="p-3 border-t border-[#262626] flex gap-2">
                <Input value={msgInput} onChange={(e) => setMsgInput(e.target.value)}
                  placeholder="Type a message…" data-testid="msg-input" maxLength={1000}
                  className="rounded-none bg-[#0A0A0A] border-[#262626] flex-1" />
                <Button type="submit" data-testid="msg-send"
                  className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold px-4">
                  <PaperPlaneRight size={18} weight="bold" />
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
